import React, { useState, useEffect } from 'react';
import {
  Row, Col, Card, Button, Tag, Modal, Form, Input, Select,
  List, Divider, message, Empty, Upload, Image } from 'antd';
import PujaSpinner from '../../components/PujaSpinner';
import { PlusOutlined, SendOutlined, UploadOutlined, PaperClipOutlined } from '@ant-design/icons';
import { buyerService } from '../../services/api';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Option } = Select;

const estadoColor    = { abierto: 'blue', en_proceso: 'orange', resuelto: 'green', cerrado: 'default' };
const prioridadColor = { baja: 'default', normal: 'blue', alta: 'orange', urgente: 'red' };
const TIPOS      = ['consulta', 'reclamo', 'correccion', 'garantia', 'soporte_tecnico'];
const PRIORIDADES = ['baja', 'normal', 'alta', 'urgente'];

const Tickets = () => {
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [cabanas, setCabanas] = useState([]);   // cabañas autorizadas (home + extras)

  // Modal nuevo ticket
  const [modalNuevo, setModalNuevo] = useState(false);
  const [formNuevo] = Form.useForm();
  const [creando, setCreando] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [destinatario, setDestinatario] = useState('cabana');

  // Modal detalle / hilo
  const [ticketActivo, setTicketActivo] = useState(null);
  const [mensajeText, setMensajeText] = useState('');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [ticketsRes, rematesRes] = await Promise.all([
        buyerService.getTickets(),
        buyerService.getRemates(),
      ]);
      setTickets(ticketsRes.data || []);

      // Deduplicar por id_clientes — incluye home cabaña (autorizado=true sin registro explícito)
      const autorizadas = (rematesRes.data || []).filter(r => r.autorizado);
      const mapa = new Map();
      autorizadas.forEach(r => {
        if (!mapa.has(r.id_clientes)) {
          mapa.set(r.id_clientes, {
            id_clientes:  r.id_clientes,
            razon_social: r.cabaña?.razon_social || `Cabaña #${r.id_clientes}`,
          });
        }
      });
      setCabanas([...mapa.values()]);
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al cargar tickets');
    } finally {
      setLoading(false);
    }
  };

  const abrirDetalle = async (id) => {
    try {
      const res = await buyerService.getTicket(id);
      setTicketActivo(res.data);
      setMensajeText('');
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al cargar ticket');
    }
  };

  const cerrarModalNuevo = () => {
    setModalNuevo(false);
    formNuevo.resetFields();
    setFileList([]);
    setDestinatario('cabana');
  };

  const crearTicket = async (values) => {
    setCreando(true);
    try {
      const res = await buyerService.crearTicket(values);
      const id_ticket = res.data.id_ticket;

      // Subir imágenes si hay archivos seleccionados
      if (fileList.length > 0) {
        try {
          await buyerService.subirImagenesTicket(id_ticket, fileList);
        } catch {
          message.warning('Ticket creado pero no se pudieron subir algunas imágenes');
        }
      }

      message.success('Ticket creado correctamente');
      cerrarModalNuevo();
      await fetchAll();
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al crear ticket');
    } finally {
      setCreando(false);
    }
  };

  const enviarMensaje = async () => {
    if (!mensajeText.trim() || !ticketActivo) return;
    setEnviando(true);
    try {
      await buyerService.responderTicket(ticketActivo.id_ticket, mensajeText);
      const res = await buyerService.getTicket(ticketActivo.id_ticket);
      setTicketActivo(res.data);
      setMensajeText('');
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al enviar mensaje');
    } finally {
      setEnviando(false);
    }
  };

  const uploadProps = {
    beforeUpload: () => false,       // no auto-upload
    fileList,
    onChange: ({ fileList: fl }) => setFileList(fl),
    accept: 'image/png,image/jpeg,image/gif,image/webp',
    multiple: true,
    listType: 'picture',
  };

  if (loading) {
    return <PujaSpinner />;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <h2 style={{ margin: 0 }}>Mis Tickets</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setModalNuevo(true)}
        >
          Nuevo Ticket
        </Button>
      </div>

      {tickets.length === 0 ? (
        <Empty description="No tenés tickets creados" />
      ) : (
        <Row gutter={[16, 16]}>
          {tickets.map((t) => (
            <Col xs={24} md={12} key={t.id_ticket}>
              <Card
                hoverable
                onClick={() => abrirDetalle(t.id_ticket)}
                title={t.asunto}
                extra={
                  <Tag color={estadoColor[t.estado] || 'default'}>
                    {t.estado}
                  </Tag>
                }
              >
                <p><strong>Tipo:</strong> {t.tipo}</p>
                <p>
                  {t.destinatario_tipo === 'comprador'
                    ? <Tag color="geekblue">Iniciado por la cabaña</Tag>
                    : <>
                        <strong>Para:</strong>{' '}
                        {t.destinatario_tipo === 'puja'
                          ? <Tag color="purple">Soporte Puja</Tag>
                          : (t.cliente?.razon_social || `#${t.id_clientes}`)}
                      </>
                  }
                </p>
                <Tag color={prioridadColor[t.prioridad] || 'blue'} style={{ marginBottom: 4 }}>
                  {t.prioridad || 'normal'}
                </Tag>
                <p style={{ color: '#999', fontSize: 12 }}>
                  {dayjs(t.created_at).format('DD/MM/YYYY HH:mm')}
                </p>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Modal: nuevo ticket */}
      <Modal
        title="Nuevo Ticket"
        open={modalNuevo}
        onCancel={cerrarModalNuevo}
        footer={null}
        destroyOnHidden
      >
        <Form form={formNuevo} layout="vertical" onFinish={crearTicket}>
          <Form.Item name="destinatario_tipo" label="Enviar a" initialValue="cabana">
            <Select onChange={(v) => { setDestinatario(v); formNuevo.setFieldValue('id_clientes', undefined); }}>
              <Option value="cabana">Una cabaña</Option>
              <Option value="puja">Soporte Puja</Option>
            </Select>
          </Form.Item>

          {destinatario === 'cabana' && (
            <Form.Item name="id_clientes" label="Cabaña" rules={[{ required: true, message: 'Requerido' }]}>
              <Select placeholder={cabanas.length === 0 ? 'Sin cabañas autorizadas' : 'Seleccioná una cabaña'}>
                {cabanas.map((c) => (
                  <Option key={c.id_clientes} value={c.id_clientes}>
                    {c.razon_social}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item name="tipo" label="Tipo" rules={[{ required: true, message: 'Requerido' }]}>
            <Select placeholder="Tipo de ticket">
              {TIPOS.map(t => <Option key={t} value={t}>{t}</Option>)}
            </Select>
          </Form.Item>

          <Form.Item name="prioridad" label="Prioridad" initialValue="normal">
            <Select>
              {PRIORIDADES.map(p => (
                <Option key={p} value={p}><Tag color={prioridadColor[p]}>{p}</Tag></Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="asunto" label="Asunto" rules={[{ required: true, message: 'Requerido' }]}>
            <Input placeholder="Asunto del ticket" />
          </Form.Item>

          <Form.Item name="descripcion" label="Descripción">
            <TextArea rows={4} placeholder="Describí tu consulta o reclamo..." />
          </Form.Item>

          <Form.Item label="Imágenes adjuntas (opcional)">
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />}>Seleccionar imágenes</Button>
            </Upload>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={creando} block>
              Crear Ticket
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal: hilo del ticket */}
      <Modal
        title={ticketActivo ? ticketActivo.asunto : ''}
        open={!!ticketActivo}
        onCancel={() => setTicketActivo(null)}
        footer={null}
        width={600}
        destroyOnHidden
      >
        {ticketActivo && (
          <>
            <Tag color={estadoColor[ticketActivo.estado] || 'default'}>
              {ticketActivo.estado}
            </Tag>
            <p style={{ marginTop: 8, color: '#666' }}>{ticketActivo.descripcion}</p>

            {/* Imágenes adjuntas al ticket */}
            {ticketActivo.imagenes?.length > 0 && (
              <>
                <Divider orientation="left" plain style={{ fontSize: 12, color: '#999' }}>
                  <PaperClipOutlined /> Imágenes adjuntas
                </Divider>
                <Image.PreviewGroup>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                    {ticketActivo.imagenes.map((img) => (
                      <Image
                        key={img.id_imagen}
                        src={img.url_imagen}
                        width={80}
                        height={80}
                        style={{ objectFit: 'cover', borderRadius: 4 }}
                      />
                    ))}
                  </div>
                </Image.PreviewGroup>
              </>
            )}

            <Divider />
            <List
              dataSource={ticketActivo.mensajes || []}
              locale={{ emptyText: 'Sin mensajes aún' }}
              renderItem={(m) => (
                <List.Item>
                  <List.Item.Meta
                    title={m.usuario?.nom_usuario || 'Usuario'}
                    description={m.mensaje}
                  />
                  <span style={{ color: '#999', fontSize: 12 }}>
                    {dayjs(m.created_at).format('DD/MM/YYYY HH:mm')}
                  </span>
                </List.Item>
              )}
            />

            {ticketActivo.estado !== 'resuelto' && (
              <>
                <Divider />
                <TextArea
                  rows={3}
                  value={mensajeText}
                  onChange={(e) => setMensajeText(e.target.value)}
                  placeholder="Escribí tu respuesta..."
                />
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  loading={enviando}
                  onClick={enviarMensaje}
                  style={{ marginTop: 8 }}
                >
                  Enviar
                </Button>
              </>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default Tickets;
